import React from "react";

/**
 * Petites briques visuelles partagées par les deux sections de la page Sauvegardes
 * (« Base de données » et « Médias ») : cartes, tableaux, pastilles et formatage FR.
 * Volontairement sans classes de tokens shadcn (bg-card, etc. : non câblées dans cet
 * admin) : uniquement des classes slate/blanc explicites.
 */

export const SectionCard: React.FC<{
	icon: React.ReactNode;
	title: string;
	description?: string;
	children: React.ReactNode;
}> = ({ icon, title, description, children }) => (
	<section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<div className="flex items-start gap-3">
			<span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
				{icon}
			</span>
			<div>
				<h2 className="text-lg font-semibold text-slate-800">{title}</h2>
				{description && <p className="text-sm text-slate-600">{description}</p>}
			</div>
		</div>
		<div className="mt-4 space-y-5">{children}</div>
	</section>
);

export const StatCard: React.FC<{ title: string; value: string; tone?: "warn" | "error" }> = ({ title, value, tone }) => (
	<div
		className={[
			"rounded-xl border border-slate-200 bg-slate-50 p-3",
			"border-l-4",
			tone === "error" ? "border-l-red-600" : tone === "warn" ? "border-l-amber-500" : "border-l-emerald-600",
		].join(" ")}
	>
		<div className="text-xs text-slate-500">{title}</div>
		<div className="text-xl font-semibold text-slate-800">{value}</div>
	</div>
);

interface TableProps {
	headers: string[];
	rows: { key: string; cells: React.ReactNode[] }[];
	emptyLabel: string;
}

export const DataTable: React.FC<TableProps> = ({ headers, rows, emptyLabel }) => {
	if (rows.length === 0) {
		return <div className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-sm italic text-slate-500">{emptyLabel}</div>;
	}
	return (
		<div className="overflow-x-auto">
			<table className="w-full border-collapse">
				<thead>
					<tr>
						{headers.map((h) => (
							<th key={h} className="border-b border-slate-200 px-2 py-1.5 text-left text-xs font-semibold text-slate-500">
								{h}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => (
						<tr key={r.key}>
							{r.cells.map((c, i) => (
								<td key={i} className="border-b border-slate-100 px-2 py-1.5 text-[13px] text-slate-700">
									{c}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export const Pill: React.FC<{ label: string; tone: "green" | "amber" | "red" | "slate" | "sky" }> = ({ label, tone }) => {
	const classes =
		tone === "green" ? "bg-emerald-100 text-emerald-700"
		: tone === "amber" ? "bg-amber-100 text-amber-700"
		: tone === "red" ? "bg-red-100 text-red-700"
		: tone === "sky" ? "bg-sky-100 text-sky-700"
		: "bg-slate-100 text-slate-600";
	return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${classes}`}>{label}</span>;
};

/** Taille lisible en unités françaises (octets → Ko/Mo/Go), virgule décimale. */
export function formatBytes(bytes: number | null | undefined): string {
	if (bytes == null || bytes === 0) return "0 o";
	const units = ["o", "Ko", "Mo", "Go", "To"];
	const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
	const value = bytes / Math.pow(1024, i);
	return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} ${units[i]}`;
}

/** Âge relatif en français (« il y a 5 min / 3 h / 2 j »), ou « jamais » si absent. */
export function formatAgeSeconds(seconds: number | null | undefined): string {
	if (seconds == null) return "jamais";
	if (seconds < 3600) return `il y a ${Math.floor(seconds / 60)} min`;
	if (seconds < 86400) return `il y a ${Math.floor(seconds / 3600)} h`;
	return `il y a ${Math.floor(seconds / 86400)} j`;
}

/** Date + heure au format français, ou tiret si absente. */
export function formatDateTime(iso: string | null | undefined): string {
	if (!iso) return "-";
	const d = new Date(iso);
	return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString("fr-FR");
}

/** Extrait le message d'erreur d'une réponse axios (payload { error } de l'API), sinon le message JS. */
export function extractApiError(err: unknown): string {
	const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
	return anyErr?.response?.data?.error ?? anyErr?.message ?? String(err);
}
